import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FormatIcon } from './menuIcons';
import { EXPORT_FORMATS, nextMenuIndex } from './moreMenuItems';

const SUBPAGES_KEY = 'apiboo.documents.exportSubpages';

function readIncludeSubpages() {
  try {
    return localStorage.getItem(SUBPAGES_KEY) !== '0';
  } catch {
    return true;
  }
}

/**
 * Export ▸ (card 3.2): 268 px, opening to the LEFT of the more-menu, level
 * with its Export entry. PDF / Markdown / HTML with a one-line description
 * each, then "Include sub-pages" (checkbox-brand, default on, remembered on
 * this device): PDF appends sub-pages as sections, Markdown and HTML export a
 * zip with one file per page.
 *
 * Keys: Up/Down/Home/End over the three formats and the checkbox; Enter or
 * Space runs a format / toggles the box; Left or Escape goes back to the
 * Export entry; Tab closes the whole menu. Opened from the keyboard
 * (`focusFirst`) PDF takes the focus; opened by hovering Export nothing is
 * highlighted and the panel itself holds the focus, so the arrows still work.
 */
export default function ExportSubmenu({ anchor, focusFirst = true, onBack, onClose, onExport }) {
  const { t } = useTranslation();
  const [includeSubpages, setIncludeSubpages] = useState(readIncludeSubpages);
  const [focus, setFocus] = useState(focusFirst ? 0 : -1);
  const [top, setTop] = useState(0);
  const panelRef = useRef(null);
  const refs = useRef([]);

  // Level with the Export entry, lifted 12 px as on the artboard.
  useLayoutEffect(() => {
    if (anchor) setTop(anchor.offsetTop - 12);
  }, [anchor]);

  useEffect(() => {
    if (focus < 0) panelRef.current?.focus({ preventScroll: true });
    else refs.current[focus]?.focus();
  }, [focus]);

  const toggle = () => {
    setIncludeSubpages((v) => {
      try {
        localStorage.setItem(SUBPAGES_KEY, v ? '0' : '1');
      } catch {
        /* this session only */
      }
      return !v;
    });
  };

  const onKeyDown = (e) => {
    e.stopPropagation();
    if (['ArrowDown', 'ArrowUp', 'Home', 'End'].includes(e.key)) {
      e.preventDefault();
      setFocus((i) => nextMenuIndex([true, true, true, true], i, e.key));
    } else if (e.key === 'ArrowLeft' || e.key === 'Escape') {
      e.preventDefault();
      onBack();
    } else if (e.key === 'Tab') {
      onClose();
    } else if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      if (focus < 0) return;
      if (focus < EXPORT_FORMATS.length) onExport(EXPORT_FORMATS[focus].id, includeSubpages);
      else toggle();
    }
  };

  return (
    <div
      ref={panelRef}
      tabIndex={-1}
      className="doc-mm doc-mm--sub"
      role="menu"
      aria-label={t('docEditor.export.label')}
      style={{ top }}
      onKeyDown={onKeyDown}
      data-testid="doc-export-menu"
    >
      {EXPORT_FORMATS.map((f, i) => (
        <button
          key={f.id}
          ref={(el) => {
            refs.current[i] = el;
          }}
          type="button"
          role="menuitem"
          tabIndex={i === focus ? 0 : -1}
          className="doc-mm-item doc-mm-item--format"
          data-format={f.id}
          onMouseEnter={() => setFocus(i)}
          onClick={() => onExport(f.id, includeSubpages)}
        >
          <FormatIcon id={f.id} />
          <span className="doc-mm-format-text">
            <span className="doc-mm-format-name">{t(f.labelKey)}</span>
            <span className="doc-mm-format-desc">{t(f.descKey)}</span>
          </span>
        </button>
      ))}
      <div className="doc-mm-sep" role="separator" />
      <label className="doc-mm-check" onMouseEnter={() => setFocus(EXPORT_FORMATS.length)}>
        <input
          ref={(el) => {
            refs.current[EXPORT_FORMATS.length] = el;
          }}
          type="checkbox"
          role="menuitemcheckbox"
          aria-checked={includeSubpages}
          className="checkbox-brand"
          tabIndex={focus === EXPORT_FORMATS.length ? 0 : -1}
          checked={includeSubpages}
          onChange={toggle}
          onKeyDown={(e) => {
            // Space toggles natively; keep Enter from submitting anything.
            if (e.key === ' ') e.stopPropagation();
          }}
        />
        <span>{t('docEditor.export.includeSubpages')}</span>
      </label>
    </div>
  );
}
